<template>
  <div id="order-query-details" class="fill-height align-start">
    <i-overlay v-if="loading" text="Loading order query details..." />

    <v-row v-else class="g-container-large" no-gutters>
      <v-dialog
        max-width="400px"
        v-model="dialog"
        class="i-dialog-header-div"
        @click:outside="((dialog = false), (answerUpdateMessage = ''))"
        @keydown.esc="((dialog = false), (answerUpdateMessage = ''))"
      >
        <v-card>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Confirmation</span>
          </v-card-title>

          <v-card-text class="py-4 text-center">
            <span v-if="hasRejectedIssues"
              ><strong>Specify reason for rejection</strong></span
            >
            <span v-else
              ><strong>Specify reason for approval (Optional)</strong></span
            >
            <br />
            <v-text-field
              append-icon="mdi-message-reply-text"
              class="pa-0 mt-4 ma-0 mb-n1"
              counter="255"
              maxlength="255"
              dense
              :label="`Message to ${isILR ? 'Customer(s)' : 'Retailer(s)'}`"
              outlined
              required
              v-model="answerUpdateMessage"
            ></v-text-field>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="i-dialog-actions-div justify-center">
            <v-btn
              rounded
              text
              class="i-btn-text-black"
              @click="((dialog = false), (answerUpdateMessage = ''))"
              >Cancel</v-btn
            >

            <v-btn
              class="i-btn-block-secondary"
              rounded
              :disabled="hasRejectedIssues && !answerUpdateMessage"
              @click="finaliseQuery()"
            >
              <strong>Finalise Claim</strong>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-col>
        <i-breadcrumbs :breadcrumbs="computedBreadcrumbs"></i-breadcrumbs>
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template v-if="$vuetifyDisplay.smAndDown">
          <v-col class="mt-2 pa-0">
            <v-card class="rounded-2 mb-6">
              <v-row no-gutters align="center" class="p-div-title py-3 px-3">
                <v-col cols="12">
                  <h3 class="g-heading-s p-title">
                    Order Query Reference Number
                    {{ `#${query.referenceNumber}` }}
                  </h3>
                </v-col>
              </v-row>

              <div class="pa-3">
                <p
                  v-if="query.notReceived"
                  class="g-bold g-border-radius-s i-status-block-red pa-2 my-2 text-center"
                >
                  ORDER NOT RECEIVED
                </p>

                <v-divider class="mt-3 mb-2"></v-divider>

                <v-row no-gutters>
                  <v-col cols="6">
                    <span><strong>PO NUMBER</strong></span>
                  </v-col>
                  <v-col cols="6" class="pl-4">
                    <span
                      ><strong>{{ query.supplierOrderNumber }}</strong></span
                    >
                  </v-col>
                </v-row>

                <v-divider class="mt-3 mb-2"></v-divider>

                <v-row no-gutters>
                  <v-col cols="6" class="py-1">
                    <span>{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ query.storeName }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>ORDER NUMBER</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ query.retailerOrderReferences[0] }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>DELIVERY ADDRESS</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ query.deliveryAddress }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>ORDER DATE & TIME</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.formatDateTime12h(query.orderDateTime)
                    }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>CREATED DATE</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.formatDateTime12h(query.createdAt)
                    }}</span>
                  </v-col>
                  <template v-if="query.resolvedAt">
                    <v-col cols="6" class="py-1">
                      <span>RESOLVED DATE</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.formatDateTime12h(query.resolvedAt)
                      }}</span>
                    </v-col>
                  </template>
                  <v-col cols="6" class="py-1">
                    <span>{{ totalLabel('ORDER TOTAL') }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.currency(
                        supplierAmount(
                          query.orderTotalExTax,
                          query.orderTotalIncTax
                        )
                      )
                    }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>PAYMENT TYPE</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <i-payment-type
                      v-if="query.paymentType"
                      :paymentType="query.paymentType"
                      :textOnly="true"
                    />
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>CREDIT PAYMENT TYPE</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <i-payment-type
                      v-if="query.creditPaymentType"
                      :paymentType="query.creditPaymentType"
                      :textOnly="true"
                    />
                  </v-col>
                </v-row>

                <v-row v-if="!isQueryUpdatable">
                  <v-col class="p-order-details text-left" cols="12">
                    <div>
                      <strong
                        >MESSAGE TO
                        {{ isILR ? 'CUSTOMER' : 'RETAILER' }}</strong
                      >
                    </div>
                    <div class="g-border-radius-s i-message">
                      <span>{{ queryMessage }}</span>
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="mb-3"></v-divider>

                <div
                  class="g-bold g-border-radius-s i-status-block-gray pa-2 my-3"
                >
                  <v-row no-gutters>
                    <v-col cols="9" class="text-right">
                      <span>{{ totalLabel('TOTAL PRODUCT CREDIT') }}:</span>
                    </v-col>
                    <v-col cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(
                          supplierAmount(
                            query.requestedCreditAmountProductExTax,
                            query.requestedCreditAmountProductIncTax
                          )
                        )
                      }}</span>
                    </v-col>
                    <v-col cols="9" class="text-right">
                      <span>{{ totalLabel('TOTAL FREIGHT') }}:</span>
                    </v-col>
                    <v-col cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(
                          supplierAmount(
                            query.requestedCreditAmountDeliveryExTax,
                            query.requestedCreditAmountDeliveryIncTax
                          )
                        )
                      }}</span>
                    </v-col>
                    <v-col cols="12">
                      <v-divider class="my-2"></v-divider>
                    </v-col>
                    <v-col cols="9" class="text-right">
                      <span>{{ totalLabel('SUPPLIER SUBTOTAL') }}:</span>
                    </v-col>
                    <v-col cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(
                          supplierAmount(
                            query.requestedCreditAmountProductExTax,
                            query.requestedCreditAmountProductIncTax
                          ) +
                            supplierAmount(
                              query.requestedCreditAmountDeliveryExTax,
                              query.requestedCreditAmountDeliveryIncTax
                            )
                        )
                      }}</span>
                    </v-col>
                    <template v-if="featureSupplierDirectRebatesEnabled">
                      <v-col cols="9" class="text-right">
                        <span
                          >REBATE
                          <template v-if="query.rebatePercentage">
                            /
                            {{
                              $filters.percentage(query.rebatePercentage)
                            }} </template
                          >{{ isUsTenant ? '' : '(INC TAX)' }}
                          <v-icon
                            class="ml-1 mb-1"
                            color="primary"
                            small
                            v-tooltip="{
                              ref: 'orderQueryRebateToolTipContent'
                            }"
                            >mdi-help-circle-outline</v-icon
                          >
                          <span ref="orderQueryRebateToolTipContent">
                            <span
                              >Rebate =
                              {{
                                $filters.percentage(query.rebatePercentage || 0)
                              }}</span
                            ><br />
                            <span
                              >Rebate % x Total Product Credit (ex Tax)</span
                            > </span
                          >:
                        </span>
                      </v-col>
                      <v-col cols="3" class="text-left pl-2">
                        <span v-if="query.requestedCreditAmountRebateIncTax > 0"
                          >-</span
                        ><span>{{
                          $filters.currency(
                            supplierAmount(
                              query.requestedCreditAmountRebateExTax,
                              query.requestedCreditAmountRebateIncTax
                            )
                          )
                        }}</span>
                      </v-col>
                    </template>
                    <v-col
                      v-if="query.requestedCreditAmountTradingTermsIncTax > 0"
                      cols="9"
                      class="text-right"
                    >
                      <span class="text-no-wrap"
                        >TRADING TERMS
                        <template v-if="query.tradingTermsPercentage">
                          /
                          {{
                            $filters.percentage(query.tradingTermsPercentage)
                          }}
                        </template>
                        <template v-if="!isUsTenant">(INC TAX)</template
                        ><v-icon
                          class="ml-1 mb-1"
                          color="primary"
                          small
                          v-tooltip="{
                            ref: 'orderQueryTradingTermsToolTipContent'
                          }"
                          >mdi-help-circle-outline</v-icon
                        >
                        <span ref="orderQueryTradingTermsToolTipContent">
                          <span
                            >Trading Terms =
                            {{
                              $filters.percentage(
                                query.tradingTermsPercentage || 0
                              )
                            }}</span
                          ><br />
                          <span
                            >Trading Terms % x Total Product Credit (ex Tax){{
                              isUsTenant ? '' : ' + Sales Tax'
                            }}</span
                          > </span
                        >:
                      </span>
                    </v-col>
                    <v-col
                      v-if="query.requestedCreditAmountTradingTermsIncTax > 0"
                      cols="3"
                      class="text-left pl-2"
                    >
                      -<span>{{
                        $filters.currency(
                          supplierAmount(
                            query.requestedCreditAmountTradingTermsExTax,
                            query.requestedCreditAmountTradingTermsIncTax
                          )
                        )
                      }}</span>
                    </v-col>
                    <v-col
                      v-if="
                        query.requestedCreditAmountProductTradingTermsIncTax > 0
                      "
                      cols="9"
                      class="text-right"
                    >
                      <span class="text-no-wrap"
                        >{{ totalLabel('TRADING TERMS') }}:</span
                      >
                    </v-col>
                    <v-col
                      v-if="
                        query.requestedCreditAmountProductTradingTermsIncTax > 0
                      "
                      cols="3"
                      class="text-left pl-2"
                    >
                      -<span>{{
                        $filters.currency(
                          supplierAmount(
                            query.requestedCreditAmountProductTradingTermsExTax,
                            query.requestedCreditAmountProductTradingTermsIncTax
                          )
                        )
                      }}</span>
                    </v-col>
                    <v-col
                      v-if="
                        query.requestedCreditAmountTenantOwnerPlatformFeeIncTax >
                        0
                      "
                      cols="9"
                      class="text-right"
                    >
                      <span>{{ totalLabel('PLATFORM FEE') }}:</span>
                    </v-col>
                    <v-col
                      v-if="
                        query.requestedCreditAmountTenantOwnerPlatformFeeIncTax >
                        0
                      "
                      cols="3"
                      class="text-left pl-2"
                    >
                      -<span>{{
                        $filters.currency(
                          supplierAmount(
                            query.requestedCreditAmountTenantOwnerPlatformFeeExTax,
                            query.requestedCreditAmountTenantOwnerPlatformFeeIncTax
                          )
                        )
                      }}</span>
                    </v-col>
                  </v-row>

                  <div class="i-total-divider"></div>

                  <v-row no-gutters>
                    <v-col cols="9" class="text-right">
                      <span>{{ totalLabel('TOTAL CREDIT AMOUNT') }}:</span>
                    </v-col>
                    <v-col cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(
                          supplierAmount(
                            query.requestedCreditAmountExTax,
                            query.requestedCreditAmountIncTax
                          )
                        )
                      }}</span>
                    </v-col>
                    <v-col v-if="!isUsTenant" cols="9" class="text-right">
                      <span>TOTAL TAXES:</span>
                    </v-col>
                    <v-col v-if="!isUsTenant" cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(query.requestedCreditAmountTotalTax)
                      }}</span>
                    </v-col>
                  </v-row>
                </div>

                <v-divider class="mt-3 mb-2"></v-divider>

                <p
                  v-if="isQueryUpdatable"
                  class="g-bold g-border-radius-s i-status-block-orange text-center pa-2 mt-2"
                >
                  QUERY STATUS: ACTION REQUIRED
                </p>
                <p
                  :class="[
                    { 'i-status-block-green': isQueryApproved },
                    { 'i-status-block-red': isQueryRejected }
                  ]"
                  class="g-bold g-border-radius-s text-center pa-2 mt-2"
                >
                  QUERY STATUS:
                  {{
                    query.status === 'APPROVED' && query.hasRejectedIssues
                      ? 'PARTIALLY'
                      : ''
                  }}
                  {{ query.status }}
                </p>

                <template v-if="isQueryUpdatable">
                  <v-divider class="mt-3 mb-2"></v-divider>

                  <v-row dense>
                    <v-col cols="6">
                      <v-btn
                        class="mt-1 mb-2 g-full-width"
                        @click="approveAll"
                        color="secondary"
                        :disabled="updating"
                        :loading="updating"
                        rounded
                        small
                      >
                        <v-icon small>mdi-check-circle-outline</v-icon> Approve
                        All
                      </v-btn>
                    </v-col>
                    <v-col cols="6">
                      <v-btn
                        class="mt-1 mb-2 g-full-width i-btn-text-red"
                        @click="rejectAll"
                        :disabled="updating"
                        :loading="updating"
                        rounded
                        small
                        text
                      >
                        <v-icon small>mdi-close-circle-outline</v-icon> Reject
                        All
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-btn
                    class="mt-1 mb-2 g-full-width i-btn-block-secondary"
                    @click="dialog = true"
                    :disabled="updating || hasOutstandingIssues"
                    large
                    :loading="updating"
                    rounded
                  >
                    Finalise Claim
                  </v-btn>
                </template>

                <div class="text-center pt-2" v-if="isQueryApproved">
                  <v-btn
                    class="g-full-width i-btn-block-primary"
                    @click="fetchPDF()"
                    :loading="fetchingPDF"
                  >
                    <span>Export PDF</span>
                    <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card>

            <v-card
              v-for="item in query.queryItems"
              :key="item.id"
              class="rounded-2 pa-4 pa-md-6 mb-6"
            >
              <p
                v-if="item.queryIssue"
                class="g-bold g-border-radius-s py-2 text-center"
                :class="[
                  {
                    'i-status-block-red': item.queryIssue.status === 'REJECTED'
                  },
                  {
                    'i-status-block-green':
                      item.queryIssue.status === 'APPROVED'
                  },
                  {
                    'i-status-block-orange':
                      item.queryIssue.status === 'OUTSTANDING'
                  }
                ]"
              >
                <strong>{{ item.queryIssue.status }}</strong>
              </p>
              <v-row class="mt-2" no-gutters>
                <v-col cols="6" class="py-1">
                  <span>{{ headersMobile[0].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.supplierProductCode }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headersMobile[1].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.productDescription }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span v-if="isVariableWeight(item)">{{
                    headersMobile[2].textVariableWeight
                  }}</span>
                  <span v-else>{{ headersMobile[2].textFixedWeight }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span v-if="isVariableWeight(item)">{{
                    $filters.currency(
                      supplierAmount(item.priceKgExGst, item.priceKgIncGst)
                    )
                  }}</span>
                  <span v-else>{{
                    $filters.currency(
                      supplierAmount(item.priceUnitExGst, item.priceUnitIncGst)
                    )
                  }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span v-if="isVariableWeight(item)">{{
                    headersMobile[3].textVariableWeight
                  }}</span>
                  <span v-else>{{ headersMobile[3].textFixedWeight }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span v-if="isVariableWeight(item)"
                    >{{ item.variableWeightInKg }} KG</span
                  >
                  <span v-else>{{ item.quantity }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headersMobile[4].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.queryType }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span v-if="isVariableWeight(item)">{{
                    headersMobile[5].textVariableWeight
                  }}</span>
                  <span v-else>{{ headersMobile[5].textFixedWeight }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span v-if="isVariableWeight(item)">
                    <template v-if="item.disputedWeightInKg === null"
                      >-</template
                    >
                    <template v-else>{{
                      `${item.disputedWeightInKg} KG`
                    }}</template>
                  </span>
                  <span v-else>{{ item.disputedQuantity || '-' }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headersMobile[6].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{
                    $filters.currency(
                      supplierAmount(
                        item.claimedCreditExTax,
                        item.claimedCredit
                      )
                    )
                  }}</span>
                </v-col>
              </v-row>

              <template v-if="!!item.queryIssue">
                <v-divider class="mt-3 mb-2"></v-divider>

                <v-btn
                  rounded
                  class="g-full-width i-btn-block-primary"
                  @click="item.showDetails = !item.showDetails"
                >
                  <template v-if="item.showDetails">
                    <v-icon left class="mt-1">mdi-arrow-up</v-icon> Hide details
                  </template>
                  <template v-else>
                    <v-icon left class="mt-1">mdi-arrow-down</v-icon> Show
                    details
                  </template>
                </v-btn>
                <v-btn
                  v-if="item.queryIssue && isQueryUpdatable"
                  class="g-full-width mt-1"
                  color="secondary"
                  @click="approve(item.queryIssue)"
                  :disabled="updating || item.queryIssue.status === 'APPROVED'"
                  :loading="updating"
                  rounded
                >
                  <v-icon left class="mt-1">mdi-check-circle-outline</v-icon>
                  Approve Issue
                </v-btn>
                <v-btn
                  v-if="item.queryIssue && isQueryUpdatable"
                  class="g-full-width mt-1"
                  color="error"
                  @click="reject(item.queryIssue)"
                  :disabled="updating || item.queryIssue.status === 'REJECTED'"
                  :loading="updating"
                  rounded
                >
                  <v-icon left class="mt-1">mdi-close-circle-outline</v-icon>
                  Reject Issue
                </v-btn>

                <v-col cols="12" v-if="item.showDetails">
                  <i-query-issue :issue="item" />
                </v-col>
              </template>
            </v-card>
          </v-col>
        </template>

        <template v-else>
          <v-card class="rounded-2 mt-4">
            <v-row no-gutters align="center" class="p-div-title pt-3 pb-3 px-4">
              <v-col cols="12" md="6">
                <h3 class="g-heading-m p-title mr-2">
                  Order Query Reference Number {{ `#${query.referenceNumber}` }}
                </h3>
              </v-col>

              <v-col v-if="isQueryApproved" cols="12" md="6" class="text-right">
                <v-btn
                  class="i-btn-block-primary"
                  @click="fetchPDF()"
                  :loading="fetchingPDF"
                >
                  <span>Export PDF</span>
                  <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <div class="pa-3">
              <v-row no-gutters>
                <v-col cols="12" md="6" lg="5" class="py-0">
                  <v-simple-table class="pa-0">
                    <tr>
                      <td>{{ isILR ? 'Customer' : 'Retailer' }}</td>
                      <td>{{ query.storeName }}</td>
                    </tr>

                    <tr>
                      <td>
                        {{ isILR ? 'Customer' : 'Retailer' }} Order Number
                      </td>
                      <td>{{ query.retailerOrderReferences[0] }}</td>
                    </tr>

                    <tr>
                      <td>Delivery Address</td>
                      <td>{{ query.deliveryAddress }}</td>
                    </tr>

                    <tr>
                      <td>Order Date & Time</td>
                      <td>
                        {{ $filters.formatDateTime12h(query.orderDateTime) }}
                      </td>
                    </tr>

                    <tr>
                      <td>Created Date</td>
                      <td>{{ $filters.formatDateTime12h(query.createdAt) }}</td>
                    </tr>

                    <tr v-if="query.resolvedAt">
                      <td>Resolved Date</td>
                      <td>
                        {{ $filters.formatDateTime12h(query.resolvedAt) }}
                      </td>
                    </tr>

                    <tr>
                      <td>{{ totalLabel('ORDER TOTAL') }}</td>
                      <td>
                        {{
                          $filters.currency(
                            supplierAmount(
                              query.orderTotalExTax,
                              query.orderTotalIncTax
                            )
                          )
                        }}
                      </td>
                    </tr>

                    <tr>
                      <td>Payment Type</td>
                      <td>
                        <i-payment-type
                          v-if="query.paymentType"
                          :paymentType="query.paymentType"
                          :textOnly="true"
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>Credit Payment Type</td>
                      <td>
                        <i-payment-type
                          v-if="query.creditPaymentType"
                          :paymentType="query.creditPaymentType"
                          :textOnly="true"
                        />
                      </td>
                    </tr>
                  </v-simple-table>
                </v-col>

                <v-spacer></v-spacer>

                <v-col cols="12" md="6" lg="4" class="py-0 text-right">
                  <v-row no-gutters>
                    <v-col cols="12">
                      <p
                        class="g-bold g-border-radius-s i-status-block-blue text-center pa-2 mb-1"
                      >
                        SUPPLIER PO NUMBER: {{ query.supplierOrderNumber }}
                      </p>
                      <p
                        v-if="query.notReceived"
                        class="g-bold g-border-radius-s i-status-block-red text-center pa-2 mb-1"
                      >
                        ORDER NOT RECEIVED
                      </p>
                      <p
                        v-else-if="isQueryUpdatable"
                        class="g-bold g-border-radius-s i-status-block-orange text-center pa-2 mb-1"
                      >
                        QUERY STATUS: ACTION REQUIRED
                      </p>
                      <p
                        v-else
                        :class="[
                          { 'i-status-block-green': isQueryApproved },
                          { 'i-status-block-red': isQueryRejected }
                        ]"
                        class="g-bold g-border-radius-s text-center pa-2 mb-1"
                      >
                        QUERY STATUS:
                        {{
                          query.status === 'APPROVED' && query.hasRejectedIssues
                            ? 'PARTIALLY'
                            : ''
                        }}
                        {{ query.status }}
                      </p>
                    </v-col>

                    <v-col cols="12">
                      <div
                        class="g-bold g-border-radius-s i-status-block-gray pa-2"
                      >
                        <v-row no-gutters>
                          <v-col cols="9" class="text-right">
                            <span
                              >{{
                                totalLabel('TOTAL PRODUCT CREDIT SUBTOTAL')
                              }}:</span
                            >
                          </v-col>
                          <v-col cols="3" class="text-left pl-2">
                            <span>{{
                              $filters.currency(
                                supplierAmount(
                                  query.requestedCreditAmountProductExTax,
                                  query.requestedCreditAmountProductIncTax
                                )
                              )
                            }}</span>
                          </v-col>
                          <v-col cols="9" class="text-right">
                            <span>{{ totalLabel('TOTAL FREIGHT') }}:</span>
                          </v-col>
                          <v-col cols="3" class="text-left pl-2">
                            <span>{{
                              $filters.currency(
                                supplierAmount(
                                  query.requestedCreditAmountDeliveryExTax,
                                  query.requestedCreditAmountDeliveryIncTax
                                )
                              )
                            }}</span>
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="my-2"></v-divider>
                          </v-col>
                          <v-col cols="9" class="text-right">
                            <span>{{ totalLabel('SUPPLIER SUBTOTAL') }}:</span>
                          </v-col>
                          <v-col cols="3" class="text-left pl-2">
                            <span>{{
                              $filters.currency(
                                supplierAmount(
                                  query.requestedCreditAmountProductExTax,
                                  query.requestedCreditAmountProductIncTax
                                ) +
                                  supplierAmount(
                                    query.requestedCreditAmountDeliveryExTax,
                                    query.requestedCreditAmountDeliveryIncTax
                                  )
                              )
                            }}</span>
                          </v-col>

                          <template v-if="featureSupplierDirectRebatesEnabled">
                            <v-col cols="9" class="text-right">
                              <span
                                >REBATE
                                <template v-if="query.rebatePercentage">
                                  /
                                  {{
                                    $filters.percentage(query.rebatePercentage)
                                  }}
                                </template>
                                <template v-if="!isUsTenant">(INC TAX)</template
                                ><v-icon
                                  class="ml-1 mb-1"
                                  color="primary"
                                  small
                                  v-tooltip="{
                                    ref: 'orderQueryRebateToolTipContent'
                                  }"
                                  >mdi-help-circle-outline</v-icon
                                >
                                <span ref="orderQueryRebateToolTipContent">
                                  <span
                                    >Rebate =
                                    {{
                                      $filters.percentage(
                                        query.rebatePercentage || 0
                                      )
                                    }}</span
                                  ><br />
                                  <span
                                    >Rebate % x Total Product Credit (ex
                                    Tax)</span
                                  > </span
                                >:
                              </span>
                            </v-col>
                            <v-col cols="3" class="text-left pl-2">
                              <span
                                v-if="
                                  query.requestedCreditAmountRebateIncTax > 0
                                "
                                >-</span
                              ><span>{{
                                $filters.currency(
                                  supplierAmount(
                                    query.requestedCreditAmountRebateExTax,
                                    query.requestedCreditAmountRebateIncTax
                                  )
                                )
                              }}</span>
                            </v-col>
                          </template>
                          <v-col
                            v-if="
                              query.requestedCreditAmountTradingTermsIncTax > 0
                            "
                            cols="9"
                            class="text-right"
                          >
                            <span
                              >TRADING TERMS
                              <template v-if="query.tradingTermsPercentage">
                                /
                                {{
                                  $filters.percentage(
                                    query.tradingTermsPercentage
                                  )
                                }}
                              </template>
                              <template v-if="!isUsTenant">(INC TAX)</template
                              ><v-icon
                                class="ml-1 mb-1"
                                color="primary"
                                small
                                v-tooltip="{
                                  ref: 'orderQueryTradingTermsToolTipContent'
                                }"
                                >mdi-help-circle-outline</v-icon
                              >
                              <span ref="orderQueryTradingTermsToolTipContent">
                                <span
                                  >Trading Terms =
                                  {{
                                    $filters.percentage(
                                      query.tradingTermsPercentage || 0
                                    )
                                  }}</span
                                ><br />
                                <span
                                  >Trading Terms % x Total Product Credit (ex
                                  Tax){{
                                    isUsTenant ? '' : ' + Sales Tax'
                                  }}</span
                                > </span
                              >:
                            </span>
                          </v-col>
                          <v-col
                            v-if="
                              query.requestedCreditAmountTradingTermsIncTax > 0
                            "
                            cols="3"
                            class="text-left pl-2"
                          >
                            -<span>{{
                              $filters.currency(
                                supplierAmount(
                                  query.requestedCreditAmountTradingTermsExTax,
                                  query.requestedCreditAmountTradingTermsIncTax
                                )
                              )
                            }}</span>
                          </v-col>
                          <v-col
                            v-if="
                              query.requestedCreditAmountProductTradingTermsIncTax >
                              0
                            "
                            cols="9"
                            class="text-right"
                          >
                            <span>{{ totalLabel('TRADING TERMS') }}:</span>
                          </v-col>
                          <v-col
                            v-if="
                              query.requestedCreditAmountProductTradingTermsIncTax >
                              0
                            "
                            cols="3"
                            class="text-left pl-2"
                          >
                            -<span>{{
                              $filters.currency(
                                supplierAmount(
                                  query.requestedCreditAmountProductTradingTermsExTax,
                                  query.requestedCreditAmountProductTradingTermsIncTax
                                )
                              )
                            }}</span>
                          </v-col>
                          <v-col
                            v-if="
                              query.requestedCreditAmountTenantOwnerPlatformFeeIncTax >
                              0
                            "
                            cols="9"
                            class="text-right"
                          >
                            <span>{{ totalLabel('PLATFORM FEE') }}:</span>
                          </v-col>
                          <v-col
                            v-if="
                              query.requestedCreditAmountTenantOwnerPlatformFeeIncTax >
                              0
                            "
                            cols="3"
                            class="text-left pl-2"
                          >
                            -<span>{{
                              $filters.currency(
                                supplierAmount(
                                  query.requestedCreditAmountTenantOwnerPlatformFeeExTax,
                                  query.requestedCreditAmountTenantOwnerPlatformFeeIncTax
                                )
                              )
                            }}</span>
                          </v-col>
                        </v-row>

                        <div class="i-total-divider"></div>

                        <v-row no-gutters>
                          <v-col cols="9" class="text-right">
                            <span
                              >{{ totalLabel('TOTAL CREDIT AMOUNT') }}:</span
                            >
                          </v-col>
                          <v-col cols="3" class="text-left pl-2">
                            <span>{{
                              $filters.currency(
                                supplierAmount(
                                  query.requestedCreditAmountExTax,
                                  query.requestedCreditAmountIncTax
                                )
                              )
                            }}</span>
                          </v-col>
                          <v-col v-if="!isUsTenant" cols="9" class="text-right">
                            <span>TOTAL TAXES:</span>
                          </v-col>
                          <v-col
                            v-if="!isUsTenant"
                            cols="3"
                            class="text-left pl-2"
                          >
                            <span>{{
                              $filters.currency(
                                query.requestedCreditAmountTotalTax
                              )
                            }}</span>
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>

                <v-col
                  v-if="!isQueryUpdatable"
                  class="p-order-details text-left my-3"
                  cols="12"
                >
                  <div>
                    <strong
                      >MESSAGE TO {{ isILR ? 'CUSTOMER' : 'RETAILER' }}</strong
                    >
                  </div>
                  <div class="g-border-radius-s i-message">
                    <span>{{ queryMessage }}</span>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-2"></v-divider>

              <v-row v-if="isQueryUpdatable" class="align-center">
                <v-col cols="12" class="text-right">
                  <v-btn
                    class="i-btn-text-red mr-1"
                    @click="rejectAll"
                    :disabled="updating"
                    :loading="updating"
                    rounded
                    small
                    text
                  >
                    <v-icon small>mdi-close-circle-outline</v-icon>
                    Reject All
                  </v-btn>

                  <v-btn
                    color="secondary"
                    @click="approveAll"
                    :disabled="updating"
                    :loading="updating"
                    rounded
                    small
                  >
                    <v-icon small>mdi-check-circle-outline</v-icon>
                    Approve All
                  </v-btn>
                </v-col>
              </v-row>

              <v-data-table
                :headers="headers"
                :items="query.queryItems"
                :items-per-page="-1"
                class="i-data-table"
                item-key="id"
                hide-default-footer
                disable-sort
              >
                <template #[`header.cost`]="{}">
                  <span
                    >{{
                      isUsTenant ? 'CARTON PRICE' : 'CARTON PRICE (INC TAX)'
                    }}
                    OR</span
                  >
                  <br />
                  <span>{{
                    isUsTenant ? 'PRICE PER KG' : 'PRICE PER KG (INC TAX)'
                  }}</span>
                </template>
                <template v-slot:item="{ item, expand, isExpanded }">
                  <tr>
                    <td>{{ item.supplierProductCode }}</td>
                    <td>{{ item.productDescription }}</td>
                    <td class="text-center">
                      <template v-if="isVariableWeight(item)"
                        >{{
                          $filters.currency(
                            supplierAmount(
                              item.priceKgExGst,
                              item.priceKgIncGst
                            )
                          )
                        }}
                        / KG</template
                      >
                      <template v-else>{{
                        $filters.currency(
                          supplierAmount(
                            item.priceUnitExGst,
                            item.priceUnitIncGst
                          )
                        )
                      }}</template>
                    </td>
                    <td class="text-center">
                      <template v-if="isVariableWeight(item)"
                        >{{ item.variableWeightInKg }} KG</template
                      >
                      <template v-else>{{ item.quantity }}</template>
                    </td>
                    <td>{{ item.queryType }}</td>
                    <td class="text-center">
                      <template v-if="isVariableWeight(item)">
                        <template v-if="item.disputedWeightInKg === null"
                          >-</template
                        >
                        <template v-else>{{
                          `${item.disputedWeightInKg} KG`
                        }}</template>
                      </template>
                      <template v-else>{{
                        item.disputedQuantity || '-'
                      }}</template>
                    </td>
                    <td class="text-center">
                      {{
                        $filters.currency(
                          supplierAmount(
                            item.claimedCreditExTax,
                            item.claimedCredit
                          )
                        )
                      }}
                    </td>
                    <td>
                      <v-btn
                        v-if="item.queryIssue"
                        color="primary"
                        @click="
                          (expand(!isExpanded),
                          (item.showDetails = !item.showDetails))
                        "
                        rounded
                        small
                      >
                        <template v-if="item.showDetails">
                          <v-icon small class="mt-1">mdi-arrow-up</v-icon> Hide
                        </template>
                        <template v-else>
                          <v-icon small class="mt-1">mdi-arrow-down</v-icon>
                          Show
                        </template>
                      </v-btn>
                    </td>
                    <td class="text-center" v-if="isQueryUpdatable">
                      <v-btn
                        v-if="item.queryIssue && isQueryUpdatable"
                        class="ml-1"
                        color="secondary"
                        @click="approve(item.queryIssue)"
                        :disabled="
                          updating || item.queryIssue.status === 'APPROVED'
                        "
                        :loading="updating"
                        rounded
                        small
                        v-tooltip="{
                          content: 'Approve Issue',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                      >
                        <v-icon>mdi-check-circle-outline</v-icon>
                      </v-btn>

                      <v-btn
                        v-if="item.queryIssue && isQueryUpdatable"
                        class="ml-1"
                        color="error"
                        @click="reject(item.queryIssue)"
                        :disabled="
                          updating || item.queryIssue.status === 'REJECTED'
                        "
                        :loading="updating"
                        rounded
                        small
                        v-tooltip="{
                          content: 'Reject Issue',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                      >
                        <v-icon>mdi-close-circle-outline</v-icon>
                      </v-btn>
                    </td>
                    <td>
                      {{ item.status }}
                      <p
                        v-if="
                          item.queryIssue &&
                          item.queryIssue.status === 'OUTSTANDING'
                        "
                        class="g-bold g-border-radius-s i-status-block-orange px-2 py-2 my-1 text-center"
                        v-resize-text="{
                          minFontSize: '13px',
                          maxFontSize: '15px'
                        }"
                      >
                        OUTSTANDING
                      </p>
                      <p
                        v-if="
                          item.queryIssue &&
                          item.queryIssue.status === 'REJECTED'
                        "
                        class="g-bold g-border-radius-s i-status-block-red px-2 py-2 my-1 text-center"
                        v-resize-text="{
                          minFontSize: '13px',
                          maxFontSize: '15px'
                        }"
                      >
                        REJECTED
                      </p>
                      <p
                        v-if="
                          item.queryIssue &&
                          item.queryIssue.status === 'APPROVED'
                        "
                        class="g-bold g-border-radius-s i-status-block-green px-2 py-2 my-1 text-center"
                        v-resize-text="{
                          minFontSize: '13px',
                          maxFontSize: '15px'
                        }"
                      >
                        APPROVED
                      </p>
                    </td>
                  </tr>
                </template>

                <template v-slot:expanded-item="{ item }">
                  <td :colspan="headers.length + 1" class="p-query-issue-td">
                    <div class="p-query-issue-div">
                      <i-query-issue :issue="item" />
                    </div>
                  </td>
                </template>
              </v-data-table>

              <template v-if="isQueryUpdatable">
                <v-divider class="my-2"></v-divider>

                <v-card-actions class="mt-n2">
                  <v-row class="align-center">
                    <v-col cols="12" class="text-right">
                      <v-btn
                        class="i-btn-block-secondary"
                        @click="dialog = true"
                        :disabled="updating || hasOutstandingIssues"
                        large
                        :loading="updating"
                        rounded
                      >
                        Finalise Claim
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-actions>
              </template>
            </div>
          </v-card>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  fetchOrderQuery,
  fetchOrderQueryPdfUrl,
  finaliseOrderQuery,
  approveOrderQueryIssue,
  rejectOrderQueryIssue
} from '@/services/orderQueries'
import iBreadcrumbs from '@/components/Breadcrumbs'
import iQueryIssue from '../components/QueryIssue'
import iOverlay from '@/components/Overlay'
import { openFile } from '@/utils/common'
import iPaymentType from '@/components/PaymentType'
import {
  buildSupplierTotalLabel,
  pickSupplierAmount
} from '@/utils/supplierAmountDisplay'

const NUM_DECIMALS_FOR_VW_KG = 2

export default {
  components: {
    iPaymentType,
    iBreadcrumbs,
    iQueryIssue,
    iOverlay
  },

  props: {
    queryId: String
  },

  data: () => ({
    loading: true,
    updating: false,
    dialog: false,
    fetchingPDF: false,
    answerStatus: '',
    answerUpdateMessage: '',
    supplierOrderNumber: '',
    storeName: '',
    confirmedDeliveryDate: '',
    deliveredDate: '',
    orderTotalIncTax: '',
    referenceNumber: '',
    status: '',
    requestedCreditAmountIncTax: '',
    query: {},
    NUM_DECIMALS_FOR_VW_KG
  }),

  computed: {
    ...mapGetters('tenant', [
      'featureSupplierDirectRebatesEnabled',
      'isILR',
      'tenantCountryRegion'
    ]),

    isUsTenant() {
      return this.tenantCountryRegion === 'US'
    },

    headersMobile() {
      return [
        {
          title: 'SUPPLIER ITEM CODE',
          key: 'supplierProductCode',
          text: 'SUPPLIER ITEM CODE',
          value: 'supplierProductCode'
        },
        {
          title: 'PRODUCT DESCRIPTION',
          key: 'productDescription',
          text: 'PRODUCT DESCRIPTION',
          value: 'productDescription'
        },
        {
          textFixedWeight: this.isUsTenant
            ? 'CARTON PRICE'
            : 'CARTON PRICE (INC TAX)',
          textVariableWeight: this.isUsTenant
            ? 'PRICE PER KG'
            : 'PRICE PER KG (INC TAX)',
          value: 'cost',
          align: 'center'
        },
        {
          textFixedWeight: 'REVISED QTY',
          textVariableWeight: 'REVISED KG',
          value: 'revisedAmount',
          align: 'center'
        },
        {
          title: 'ISSUE TYPE',
          key: 'queryType',
          text: 'ISSUE TYPE',
          value: 'queryType',
          align: 'center'
        },
        {
          textFixedWeight: 'CREDIT QUANTITY',
          textVariableWeight: 'CREDIT WEIGHT (KG)',
          value: 'disputedAmount',
          align: 'center'
        },
        {
          title: buildSupplierTotalLabel(
            this.tenantCountryRegion,
            'PRODUCT CREDIT AMOUNT'
          ),
          key: 'claimedCredit',
          text: buildSupplierTotalLabel(
            this.tenantCountryRegion,
            'PRODUCT CREDIT AMOUNT'
          ),
          value: 'claimedCredit',
          align: 'center'
        },
        {
          title: 'ISSUE REPORT',
          key: 'viewIssue',
          text: 'ISSUE REPORT',
          value: 'viewIssue',
          align: 'center'
        }
      ]
    },

    headers() {
      return [
        {
          title: 'SUPPLIER ITEM CODE',
          key: 'supplierProductCode',
          text: 'SUPPLIER ITEM CODE',
          value: 'supplierProductCode'
        },
        {
          title: 'PRODUCT DESCRIPTION',
          key: 'productDescription',
          text: 'PRODUCT DESCRIPTION',
          value: 'productDescription'
        },
        {
          title: this.isUsTenant
            ? 'CARTON PRICE OR PRICE PER KG'
            : 'CARTON PRICE (INC TAX) OR PRICE PER KG (INC TAX)',
          key: 'cost',
          text: this.isUsTenant
            ? 'CARTON PRICE OR PRICE PER KG'
            : 'CARTON PRICE (INC TAX) OR PRICE PER KG (INC TAX)',
          value: 'cost',
          align: 'center'
        },
        {
          title: 'REVISED QTY OR KG',
          key: 'revisedAmount',
          text: 'REVISED QTY OR KG',
          value: 'revisedAmount',
          align: 'center'
        },
        {
          title: 'ISSUE TYPE',
          key: 'queryType',
          text: 'ISSUE TYPE',
          value: 'queryType'
        },
        {
          title: 'CREDIT QTY OR WEIGHT (KG)',
          key: 'disputedAmount',
          text: 'CREDIT QTY OR WEIGHT (KG)',
          value: 'disputedAmount',
          align: 'center'
        },
        {
          title: this.isUsTenant
            ? 'PRODUCT CREDIT\nAMOUNT'
            : 'PRODUCT CREDIT\nAMOUNT (INC TAX)',
          key: 'claimedCredit',
          text: this.isUsTenant
            ? 'PRODUCT CREDIT\nAMOUNT'
            : 'PRODUCT CREDIT\nAMOUNT (INC TAX)',
          value: 'claimedCredit',
          align: 'center'
        },
        {
          title: '',
          key: '',
          text: '',
          value: '',
          align: 'center',
          width: 100
        },
        {
          title: 'ACTIONS',
          key: 'viewIssue',
          text: 'ACTIONS',
          value: 'viewIssue',
          align: 'center',
          width: 140,
          updatableQueryOnly: true
        },
        {
          title: 'STATUS',
          key: 'status',
          text: 'STATUS',
          value: 'status',
          align: 'center',
          width: 140
        }
      ].filter((h) => !h.updatableQueryOnly || this.isQueryUpdatable)
    },
    computedBreadcrumbs() {
      let breadcrumbs = []
      if (this.isQueryUpdatable) {
        breadcrumbs = [
          { text: 'Home', exact: true, to: { name: 'dashboard' } },
          { text: 'Order Queries', exact: true, to: { name: 'order-queries' } },
          { text: this.$route.meta.title, to: null }
        ]
      } else {
        breadcrumbs = [
          { text: 'Home', exact: true, to: { name: 'dashboard' } },
          { text: 'Reports', exact: true, to: { name: 'reports' } },
          {
            text: 'Order Queries History',
            exact: true,
            to: { name: 'order-queries-history' }
          },
          { text: this.$route.meta.title, to: null }
        ]
      }
      return breadcrumbs
    },

    isQueryUpdatable() {
      return this.query.status === 'OUTSTANDING'
    },

    isQueryApproved() {
      return this.query.status === 'APPROVED'
    },

    isQueryRejected() {
      return this.query.status === 'REJECTED'
    },

    queryMessage() {
      return this.query.statusUpdateMessage || 'None'
    },

    queryIssues() {
      return this.query.queryItems
        ?.filter((i) => !!i.queryIssue)
        .map((i) => i.queryIssue)
    },

    hasOutstandingIssues() {
      return this.queryIssues.some((i) => i.status === 'OUTSTANDING')
    },

    hasRejectedIssues() {
      return this.queryIssues.some((i) => i.status === 'REJECTED')
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    totalLabel(baseLabel) {
      return buildSupplierTotalLabel(this.tenantCountryRegion, baseLabel)
    },

    supplierAmount(exTax, incTax) {
      return pickSupplierAmount({
        tenantCountryRegion: this.tenantCountryRegion,
        exTax,
        incTax
      })
    },

    isVariableWeight(product) {
      return product.weightType === 'VARIABLE_WEIGHT'
    },

    toggleQueryItemDetails(item, expand, isExpanded) {
      expand(!isExpanded)
      item.showDetails = !item.showDetails
    },

    closeFinaliseDialog() {
      this.dialog = false
      this.answerUpdateMessage = ''
    },

    approveAll() {
      this.updating = true
      const queryId = this.queryId
      Promise.all(
        this.queryIssues.map((issue) =>
          approveOrderQueryIssue(queryId, issue.id)
        )
      ).then(() => {
        fetchOrderQuery(queryId)
          .then((query) => {
            this.query = this.mapQuery(query)
          })
          .catch(() => {
            this.addNotification({ color: 'error', defaultError: true })
          })
          .finally(() => {
            this.updating = false
          })
      })
    },

    approve(issue) {
      this.updating = true

      approveOrderQueryIssue(this.query.id, issue.id)
        .then((query) => {
          this.query = this.mapQuery(query)
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Order Query issue could not be updated...'
          })
        })
        .finally(() => {
          this.updating = false
        })
    },

    rejectAll() {
      this.updating = true
      const queryId = this.queryId
      Promise.all(
        this.queryIssues.map((issue) =>
          rejectOrderQueryIssue(queryId, issue.id)
        )
      ).then(() => {
        fetchOrderQuery(queryId)
          .then((query) => {
            this.query = this.mapQuery(query)
          })
          .catch(() => {
            this.addNotification({ color: 'error', defaultError: true })
          })
          .finally(() => {
            this.updating = false
          })
      })
    },

    reject(issue) {
      this.updating = true

      rejectOrderQueryIssue(this.query.id, issue.id)
        .then((query) => {
          this.query = this.mapQuery(query)
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Order Query issue could not be updated...'
          })
        })
        .finally(() => {
          this.updating = false
        })
    },

    finaliseQuery() {
      this.loading = true

      const payload = {
        statusUpdateMessage: this.answerUpdateMessage
      }

      finaliseOrderQuery(this.queryId, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Order Query status successfully updated'
          })
          this.closeFinaliseDialog()
          this.fetch()
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Order Query status could not be updated...'
          })
          this.loading = false
          this.closeFinaliseDialog()
        })
    },

    queryType(product) {
      let text = ''

      switch (product.queryType) {
        case 'INCORRECT_PRODUCT':
          text = 'Incorrect Product'
          break
        case 'DAMAGED':
          text = 'Damaged Product'
          break
        case 'MISSING_QUANTITY':
          text = product.variableWeightInKg
            ? 'Missing / Short Weight (KG)'
            : 'Missing / Short Quantity'
          break
        case 'OTHER':
          text = 'Other'
          break
        default:
          text = '-'
      }

      return text
    },

    fetch() {
      this.loading = true
      fetchOrderQuery(this.queryId)
        .then((query) => {
          this.query = this.mapQuery(query)
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchPDF() {
      this.fetchingPDF = true
      fetchOrderQueryPdfUrl(this.query.id)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Invoice ${this.query.id} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetchingPDF = false
        })
    },

    getDeliveryAddress(address) {
      if (!address) return 'Not provided'
      return `${address.line1} ${address.suburb} ${address.state} ${address.postCode}`
    },

    mapQuery(query) {
      return {
        ...query,
        deliveryAddress: this.getDeliveryAddress(query.storeDeliveryAddress),
        queryItems: query.queryItems.map((item) => ({
          ...item,
          weightType:
            item.variableWeightInKg >= 0 ? 'VARIABLE_WEIGHT' : 'FIXED_WEIGHT',
          queryType: this.queryType(item),
          quantity: item.unitQty,
          disputedQuantity: item.disputedQty,
          variableWeightInKg: this.maxNumberDecimals(
            item.variableWeightInKg,
            NUM_DECIMALS_FOR_VW_KG
          ),
          disputedWeightInKg: this.maxNumberDecimals(
            item.disputedWeightInKg,
            NUM_DECIMALS_FOR_VW_KG
          )
        }))
      }
    },

    maxNumberDecimals(value = null, numberDecimals) {
      if (value === null || value < 0) return null
      return Number(value).toFixed(numberDecimals)
    }
  },

  mounted() {
    this.fetch()
  }
}
</script>

<style lang="scss" scoped>
#order-query-details {
  table {
    border-collapse: collapse;
    tr > td {
      padding: 2px 0px;
    }
    tr > td:nth-of-type(1) {
      font-weight: bold;
    }
  }
  .p-div-title {
    background-color: $primary-color-light;
    color: $primary-color;
  }
  .p-title {
    display: inline-block;
    letter-spacing: 0.75px;
  }
}

@media only screen and (min-width: 960px) {
  #order-query-details {
    .p-query-issue-td {
      margin: 0px !important;
      padding: 0px !important;
    }
    .p-query-issue-div {
      $border-color: $primary-color;
      background-color: hsla(203, 100%, 95%, 0.4);
      border-top: 1px solid $border-color;
      border-bottom: 1px solid $border-color;
      border-left: 1px solid $border-color;
      border-right: 1px solid $border-color;
      border-radius: 0px 0px 5px 5px;
      margin: 0px 0px 32px 0px;
      padding: 8px;
    }
  }
}
</style>
